import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="webcam"
export default class extends Controller {
    static targets = ["modal", "video", "canvas", "captureBtn", "countdown", "countdownText", "error", "errorMessage"]

    connect() {
        this.stream = null
        this.countdownTimer = null

        // Listen for open event
        this.boundOpen = () => this.open()
        window.addEventListener('webcam:open', this.boundOpen)
    }

    async open() {
        this.modalTarget.classList.remove('hidden')
        await this.startCamera()
    }

    close() {
        this.stopCamera()
        this.modalTarget.classList.add('hidden')
        this.hideError()
    }

    async startCamera() {
        try {
            // Try to get rear camera on mobile, fallback to user camera
            const constraints = {
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                audio: false
            }

            this.stream = await navigator.mediaDevices.getUserMedia(constraints)
            this.videoTarget.srcObject = this.stream
            await this.videoTarget.play()

            this.captureBtnTarget.disabled = false
        } catch (error) {
            console.error('Camera access error:', error)
            this.showError(error)
            this.captureBtnTarget.disabled = true
        }
    }

    stopCamera() {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop())
            this.videoTarget.srcObject = null
            this.stream = null
        }
    }

    async capture() {
        // Show countdown
        await this.startCountdown()

        // Capture the photo
        const canvas = this.canvasTarget
        const video = this.videoTarget

        // Set canvas size to match video
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        // Draw current video frame to canvas
        const context = canvas.getContext('2d')
        context.drawImage(video, 0, 0, canvas.width, canvas.height)

        // Convert canvas to blob and pass to post-form (don't upload yet)
        canvas.toBlob((blob) => {
            if (blob) {
                // Create a File from the Blob
                const filename = `webcam_${Date.now()}.jpg`
                const file = new File([blob], filename, { type: 'image/jpeg' })
                
                // Dispatch event to notify post form with the File (not uploaded)
                const event = new CustomEvent('webcam:captured', {
                    detail: { file: file }
                })
                window.dispatchEvent(event)
                
                this.close()
            }
        }, 'image/jpeg', 0.92)
    }

    async startCountdown() {
        return new Promise((resolve) => {
            let count = 3
            this.countdownTextTarget.textContent = count
            this.countdownTarget.classList.remove('hidden')

            this.countdownTimer = setInterval(() => {
                count--
                if (count > 0) {
                    this.countdownTextTarget.textContent = count
                } else {
                    clearInterval(this.countdownTimer)
                    this.countdownTarget.classList.add('hidden')
                    resolve()
                }
            }, 1000)
        })
    }

    showError(error) {
        let message = 'Unable to access camera. '

        if (error.name === 'NotAllowedError') {
            message += 'Please allow camera access in your browser settings.'
        } else if (error.name === 'NotFoundError') {
            message += 'No camera found on this device.'
        } else if (error.name === 'NotReadableError') {
            message += 'Camera is already in use by another application.'
        } else {
            message += 'Please check your camera permissions and try again.'
        }

        this.errorMessageTarget.textContent = message
        this.errorTarget.classList.remove('hidden')
        this.videoTarget.classList.add('hidden')
    }

    hideError() {
        this.errorTarget.classList.add('hidden')
        this.videoTarget.classList.remove('hidden')
    }

    disconnect() {
        this.stopCamera()
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer)
        }
        if (this.boundOpen) {
            window.removeEventListener('webcam:open', this.boundOpen)
        }
    }
}

